import { z } from "zod";
/**
 * The canonical run record: V2's run-event vocabulary (docs/runtime/EVENT-CONTRACT.md in
 * the V2 tree) turned into a CLOSED enum with contractual payloads. Every adapter —
 * Anthropic, Foundry, OpenRouter — emits exactly these; the console keeps reading the
 * names it already knows. Provider ids never appear in a payload: the adapter translates,
 * the platform mints ids. The enum is the superset: an engine may leave a type unused
 * (the loops never reschedule a thread), never add one of its own.
 *
 * The sink checks every row whose type is in the enum against its payload (sink.ts) and
 * refuses an off-contract row: it is never written, and it raises an `[ops-alert]` naming
 * the run, the type and the field (owner decision B-D6).
 *
 * Every event of every thread belongs to the run: a row of a helper thread carries that
 * thread's `session_thread_id`, a row of the primary carries none.
 *
 * Dropped from V2 on purpose (written but never read): `span.model_request_start`,
 * `span.start`, `platform.output_recovered`. Outside the enum as well: `session.usage` and
 * `session.updated`, which may be stored but are never streamed. The `status` frame is a
 * stream projection, not an event, and never becomes a row.
 */
export const RUN_EVENT_TYPES = [
    // The session's own lifecycle (platform-authored on the loops, translated on Anthropic).
    "session.status_running",
    "session.status_idle",
    "session.status_terminated",
    "session.status_rescheduled",
    "session.error",
    "session.thread_created",
    "session.thread_status_running",
    "session.thread_status_idle",
    "session.thread_status_terminated",
    "session.thread_status_rescheduled",
    // What the agent said and did.
    "agent.message",
    "agent.thinking",
    "agent.tool_use",
    "agent.mcp_tool_use",
    "agent.custom_tool_use",
    "agent.tool_result",
    "agent.mcp_tool_result",
    "agent.thread_message_sent",
    "agent.thread_message_received",
    "agent.thread_context_compacted",
    // What the person said and decided (platform-minted, never provider-echoed).
    "user.message",
    "user.define_outcome",
    "user.tool_confirmation",
    "user.custom_tool_result",
    "user.interrupt",
    // Dynamic workflow runs (Phase G): the platform's on Anthropic, the coordinator runtime's on Foundry.
    "workflow_run.created",
    "workflow_run.status_running",
    "workflow_run.status_idle",
    "workflow_run.status_ended",
    "workflow_run.error",
    "workflow_run.phase_started",
    "workflow_run.phase_ended",
    // Measurements and the completion cycle.
    "span.model_request_end",
    "span.outcome_evaluation_start",
    "span.outcome_evaluation_ongoing",
    "span.outcome_evaluation_end",
];
export const RunEventTypeSchema = z.enum(RUN_EVENT_TYPES);
export function isRunEventType(value) {
    return typeof value === "string" && RUN_EVENT_TYPES.includes(value);
}
export const RUN_EVENT_SOURCES = ["user", "agent", "platform"];
/** The source is a function of the type: user.* is the person, agent.* the model, the rest the platform. */
export function runEventSource(type) {
    if (type.startsWith("user."))
        return "user";
    if (type.startsWith("agent."))
        return "agent";
    return "platform";
}
/**
 * Why a session went idle. V2 accepted aliases (`interrupted`, `max_iterations`,
 * `max_iterations_reached`); adapters normalize to these, which are the values the
 * console branches on.
 */
export const STOP_REASONS = [
    "end_turn",
    "requires_action",
    "user_interrupt",
    "retries_exhausted",
    "max_turns",
    "satisfied",
    "needs_revision",
    // A pause at the run's dollar cap (the loop engines and CMA both send it).
    "budget_reached",
    // CMA: the model declined the request; the idle may carry `stop_details` with its category.
    "refusal",
];
export const StopReasonSchema = z.enum(STOP_REASONS);
/** The completion cycle's verdict (V2's 16 aliases normalize to these five). */
export const OUTCOME_RESULTS = [
    "satisfied",
    "needs_revision",
    "failed",
    "interrupted",
    "max_iterations_reached",
];
export const OutcomeResultSchema = z.enum(OUTCOME_RESULTS);
export const TextBlockSchema = z.object({ type: z.literal("text"), text: z.string() });
export const SearchResultBlockSchema = z.object({
    type: z.literal("search_result"),
    title: z.string().optional(),
    source: z.string().optional(),
    content: z.array(TextBlockSchema),
});
/** Where a file block's bytes are: base64 or plain text inline, or a URL; kept as the provider sent it. */
const BlockSourceSchema = z.looseObject({ type: z.string().min(1) });
/** An image a tool handed back (Claude's read of a PNG or JPEG). */
export const ImageBlockSchema = z.object({ type: z.literal("image"), source: BlockSourceSchema });
/** A document a tool handed back (Claude's web fetch of a page or a PDF). */
export const DocumentBlockSchema = z.object({
    type: z.literal("document"),
    title: z.string().nullish(),
    source: BlockSourceSchema,
});
export const ContentBlockSchema = z.discriminatedUnion("type", [
    TextBlockSchema,
    SearchResultBlockSchema,
    ImageBlockSchema,
    DocumentBlockSchema,
]);
/** Claude withholds its advisor's words: the advice, and both copies of it, arrive as this block. */
export const RedactedBlockSchema = z.object({ type: z.literal("redacted") });
/** What an agent says, to the person or to another thread. */
export const MessageBlockSchema = z.discriminatedUnion("type", [
    TextBlockSchema,
    RedactedBlockSchema,
]);
export const ModelUsageSchema = z.object({
    input_tokens: z.number().int().nonnegative(),
    output_tokens: z.number().int().nonnegative(),
    cache_creation_input_tokens: z.number().int().nonnegative().optional(),
    cache_read_input_tokens: z.number().int().nonnegative().optional(),
});
/**
 * No contractual field. The stored row also carries its envelope (`id`, `type`,
 * `processed_at`) and, on Anthropic, the platform's own extras: unknown keys are stripped,
 * never refused, as on every other payload.
 */
const Empty = z.object({});
/** Set on a row that belongs to a helper thread; absent on the primary's rows. */
const Tagged = z.object({ session_thread_id: z.string().min(1).optional() });
/**
 * The thread a lifecycle row is about. The wire value is a per-run alias (t1, t2 …);
 * stored rows hold the engine's id. `anthropic.advisor` is the reserved advisor name on
 * every engine.
 */
const ThreadRef = z.object({
    session_thread_id: z.string().min(1),
    agent_name: z.string().optional(),
});
/**
 * A thread's stop reason: a plain string on purpose, so the thread row is not coupled to
 * STOP_REASONS (the loops already send `budget_reached` on a thread).
 */
const ThreadStop = z.object({
    stop_reason: z.object({
        type: z.string().min(1),
        event_ids: z.array(z.string().min(1)).optional(),
    }),
});
/** A source the answer cites (V5's `x_citations`); the block and range locate it in the text. */
export const CitationSchema = z.object({
    url: z.string().url(),
    title: z.string().nullable(),
    block: z.number().int().nonnegative().optional(),
    start_index: z.number().int().nonnegative().optional(),
    end_index: z.number().int().nonnegative().optional(),
});
const ToolUse = Tagged.extend({
    /** THE tool-call identity: answers, results and pauses refer to it. */
    id: z.string().min(1),
    name: z.string().min(1),
    input: z.record(z.string(), z.unknown()),
    /**
     * Absent on a call that needed no decision; `ask` is what pauses a run, `deny` is Claude's
     * permission policy refusing the call.
     */
    evaluated_permission: z.enum(["ask", "allow", "deny"]).optional(),
    mcp_server_name: z.string().optional(),
    /**
     * Where the call ran: `native` = the provider's own runtime (Claude's built-ins and MCP),
     * `hosted` = a Foundry hosted tool, `platform` = V5's harness. Claude's relayed rows leave
     * it out, which means native. Cost records key off it.
     */
    execution: z.enum(["native", "hosted", "platform"]).optional(),
});
const ToolResult = Tagged.extend({
    tool_use_id: z.string().min(1),
    custom_tool_use_id: z.string().optional(),
    is_error: z.boolean(),
    content: z.array(ContentBlockSchema),
});
/** An MCP call's result names its call by `mcp_tool_use_id`, as every engine writes it. */
const McpToolResult = Tagged.extend({
    mcp_tool_use_id: z.string().min(1),
    is_error: z.boolean(),
    content: z.array(ContentBlockSchema),
});
/** 0-indexed revision cycle: 0 is the first evaluation (Claude's and the loops' count). */
const Iteration = z.number().int().nonnegative();
/** The `workflow_run.status_ended` result types this contract gives a shape. */
const KNOWN_WORKFLOW_RESULTS = new Set(["completed", "stopped", "error"]);
/** Payload per type. The sink validates every row against this before it is written. */
export const RUN_EVENT_PAYLOADS = {
    "session.status_running": Empty,
    "session.status_idle": z.object({
        stop_reason: z.object({
            type: StopReasonSchema,
            /** On `requires_action`: the tool-call event ids the pause is blocked on. */
            event_ids: z.array(z.string().min(1)).optional(),
        }),
    }),
    "session.status_terminated": Empty,
    "session.status_rescheduled": Empty,
    "session.error": Tagged.extend({
        error: z.object({
            type: z.string().min(1),
            message: z.string().optional(),
            mcp_server_name: z.string().optional(),
            retry_status: z.object({ type: z.string().min(1) }).optional(),
        }),
    }),
    /** The one row that names a thread's agent; every other row names the thread only. */
    "session.thread_created": ThreadRef.extend({
        agent_name: z.string().min(1),
        /** The workflow run that started the thread, when one did. */
        workflow_run_id: z.string().min(1).nullable().optional(),
    }),
    "session.thread_status_running": ThreadRef,
    "session.thread_status_idle": ThreadRef.extend(ThreadStop.shape),
    "session.thread_status_terminated": ThreadRef,
    "session.thread_status_rescheduled": ThreadRef,
    "workflow_run.created": z.object({
        workflow_run_id: z.string().min(1),
        name: z.string(),
        description: z.string().nullable(),
        phases: z.array(z.object({ id: z.string().min(1), name: z.string(), description: z.string().nullable() })),
    }),
    "workflow_run.status_running": z.object({ workflow_run_id: z.string().min(1) }),
    "workflow_run.status_idle": z.object({ workflow_run_id: z.string().min(1) }),
    "workflow_run.status_ended": z.object({
        workflow_run_id: z.string().min(1),
        result: z.union([
            z.object({ type: z.literal("completed") }),
            z.object({ type: z.literal("stopped") }),
            z.object({
                type: z.literal("error"),
                error: z.object({ type: z.string().min(1), message: z.string() }),
            }),
            // A result type this contract does not know yet is a run that ended some other way
            // (the Workflow runs page). It is kept whole: refusing it would drop the event that
            // closes the run. A known type still has to take its own shape.
            z.looseObject({
                type: z
                    .string()
                    .min(1)
                    .refine((type) => !KNOWN_WORKFLOW_RESULTS.has(type)),
            }),
        ]),
    }),
    "workflow_run.error": z.object({
        workflow_run_id: z.string().min(1).nullable(),
        error: z.object({ type: z.string().min(1), message: z.string() }),
    }),
    "workflow_run.phase_started": z.object({
        workflow_run_id: z.string().min(1),
        workflow_run_phase_id: z.string().min(1),
    }),
    "workflow_run.phase_ended": z.object({
        workflow_run_id: z.string().min(1),
        workflow_run_phase_id: z.string().min(1),
        phase_started_id: z.string().min(1),
    }),
    "agent.message": Tagged.extend({
        content: z.array(MessageBlockSchema),
        x_citations: z.array(CitationSchema).optional(),
    }),
    /** The loops emit a progress signal with no text; a model that returns its thinking fills it. */
    "agent.thinking": Tagged.extend({ content: z.array(TextBlockSchema).optional() }),
    "agent.tool_use": ToolUse,
    "agent.mcp_tool_use": ToolUse,
    "agent.custom_tool_use": Tagged.extend({
        id: z.string().min(1),
        name: z.literal("check_with_me"),
        input: z.object({
            question: z.string().min(1),
            context: z.string().optional(),
            responseMode: z.enum(["text", "choice", "confirmation"]),
            choices: z.array(z.string().min(1)).min(2).max(4).optional(),
            quickReplies: z.array(z.string().min(1)).min(1).max(3).optional(),
        }),
    }),
    "agent.tool_result": ToolResult,
    "agent.mcp_tool_result": McpToolResult,
    /** Lead-side rows are untagged; Claude also logs the helper's own copy, tagged with its thread. */
    "agent.thread_message_sent": Tagged.extend({
        to_session_thread_id: z.string().optional(),
        to_agent_name: z.string().optional(),
        content: z.array(MessageBlockSchema),
    }),
    "agent.thread_message_received": Tagged.extend({
        from_session_thread_id: z.string().optional(),
        from_agent_name: z.string().optional(),
        content: z.array(MessageBlockSchema),
    }),
    "agent.thread_context_compacted": Tagged,
    /** Block 0 is the person's text; block 1, when present, is the attachment manifest. */
    "user.message": z.object({ content: z.array(TextBlockSchema).min(1) }),
    "user.define_outcome": z.object({
        outcome_id: z.string().min(1),
        description: z.string(),
        max_iterations: z.number().int().positive(),
        rubric: z.object({ content: z.string() }).optional(),
    }),
    "user.tool_confirmation": z.object({
        tool_use_id: z.string().min(1),
        result: z.enum(["allow", "deny"]),
        deny_message: z.string().optional(),
    }),
    "user.custom_tool_result": z.object({
        custom_tool_use_id: z.string().min(1),
        content: z.array(TextBlockSchema).min(1),
        is_error: z.boolean(),
    }),
    "user.interrupt": Empty,
    /** On the thread that made the request; no model or provider id (the run's roster names it). */
    "span.model_request_end": Tagged.extend({
        /** The preview this request streamed under, when there was one. */
        model_request_start_id: z.string().optional(),
        model_usage: ModelUsageSchema,
    }),
    "span.outcome_evaluation_start": z.object({
        outcome_id: z.string().min(1),
        iteration: Iteration,
    }),
    "span.outcome_evaluation_ongoing": z.object({
        outcome_id: z.string().min(1),
        /** Claude's heartbeat carries it; the loops' does not yet. */
        iteration: Iteration.optional(),
    }),
    "span.outcome_evaluation_end": z.object({
        outcome_id: z.string().min(1),
        iteration: Iteration,
        result: OutcomeResultSchema,
        explanation: z.string().optional(),
        /** The check's own tokens, when a model was asked. */
        usage: ModelUsageSchema.optional(),
    }),
};
export const RunEventSchema = z.discriminatedUnion("type", RUN_EVENT_TYPES.map((type) => z.object({ type: z.literal(type), payload: RUN_EVENT_PAYLOADS[type] })));
/** Validate a (type, payload) pair; the sink's write gate. Throws on anything off-contract. */
export function parseRunEvent(type, payload) {
    if (!isRunEventType(type))
        throw new Error(`unknown run event type ${type}`);
    return { type, payload: RUN_EVENT_PAYLOADS[type].parse(payload) };
}
/* ---------------- the organization's per-minute limit (the Workflow runs page) ---------------- */
/**
 * The run's stop reason when the organization's per-minute limit stopped a thread and the
 * turn then ended: the run stays open (`requires_action`) for the person, who waits a minute
 * and then asks the agent to continue (workflow-runs.md:835). V5's name, never an idle's.
 */
export const RATE_LIMITED_STOP = "org_rate_limited";
/**
 * A `session.error` payload saying the organization's per-minute limit stopped its thread
 * (workflow-runs.md:835: the message names a rate limit). A model request's own limit or
 * overload (`model_rate_limited_error`, `model_overloaded_error`, :830-833) is not this one,
 * nor is an error the server is still retrying, nor one V5's loop wrote about a request it
 * retries itself (`x_retryable`, its own field).
 */
export function isOrgRateLimitStop(payload) {
    const row = payload;
    if (row?.x_retryable !== undefined)
        return false;
    const error = row?.error;
    if (!error || typeof error !== "object")
        return false;
    const { type, message, retry_status: retry } = error;
    if (type === "model_rate_limited_error" || type === "model_overloaded_error")
        return false;
    if (retry?.type === "retrying")
        return false;
    return typeof message === "string" && /rate[\s_-]?limit/i.test(message);
}
/** A thread working again after the limit stopped it: its own agent row, or its running row. */
const WORKS_AGAIN = new Set([
    "agent.message",
    "agent.thinking",
    "agent.tool_use",
    "agent.mcp_tool_use",
    "agent.custom_tool_use",
    "session.thread_status_running",
]);
/**
 * Folds one row into the threads the per-minute limit stopped, by thread (`""` the primary):
 * an org-limit `session.error` stops its thread, the thread working again takes it out, and
 * the person's message (the ask to continue) clears them all. Answers the thread a stop names,
 * null for every other row.
 */
export function trackOrgRateLimit(stopped, type, payload) {
    if (type === "user.message") {
        stopped.clear();
        return null;
    }
    const tag = payload?.session_thread_id;
    const thread = typeof tag === "string" ? tag : "";
    if (type === "session.error" && isOrgRateLimitStop(payload)) {
        stopped.add(thread);
        return thread;
    }
    if (WORKS_AGAIN.has(type))
        stopped.delete(thread);
    return null;
}
/**
 * Whether an idle with this stop reason is the per-minute limit's: a thread it stopped has not
 * worked again, and the turn ended (`end_turn`, or `retries_exhausted` on the error). A pause
 * and every other ending keep their own reading.
 */
export function idleAtOrgRateLimit(stopped, stop) {
    return stopped.size > 0 && (stop === "end_turn" || stop === "retries_exhausted");
}
//# sourceMappingURL=run-events.js.map